import { useState } from 'react';
import { joinWaitlist } from '@/lib/api';
import { useT, useLang } from '@/lib/i18n';
import { track } from '@/lib/analytics';
import { toast } from 'sonner';

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

export const WaitlistForm = ({ source = 'unknown', variant = 'hero', className = '' }) => {
  const t = useT();
  const { lang } = useLang();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const validate = (value) => {
    const v = (value || '').trim();
    if (!v) return t('errors.email_required');
    if (v.length > 254) return t('errors.email_too_long');
    if (!EMAIL_RE.test(v)) return t('errors.email_invalid');
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    const err = validate(email);
    if (err) {
      setErrorMsg(err);
      toast.error(err, { duration: 5000 });
      return;
    }
    setErrorMsg('');
    setLoading(true);
    const res = await joinWaitlist({ email: email.trim(), source, lang });
    setLoading(false);
    if (res.ok) {
      setSuccess(true);
      setEmail('');
      track('waitlist_joined', { lang });
      toast.success(t('waitlist.success'), { duration: 5000 });
    } else if (res.alreadyIn) {
      toast(t('waitlist.already_in'), { icon: '✨', duration: 5000 });
      setEmail('');
    } else {
      const msg = t(`errors.${res.code || 'generic'}`);
      setErrorMsg(msg);
      toast.error(msg, { duration: 5000 });
    }
  };

  const isCta = variant === 'cta';

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      data-testid={`waitlist-form-${variant}`}
      className={`flex w-full flex-col gap-3 sm:flex-row sm:items-start ${className}`}
    >
      <div className="flex-1">
        <label htmlFor={`waitlist-email-${variant}`} className="sr-only">
          {t('contact.email_label')}
        </label>
        <input
          id={`waitlist-email-${variant}`}
          type="text"
          inputMode="email"
          autoComplete="email"
          placeholder={t('common.email_placeholder')}
          value={email}
          onChange={(e) => { setEmail(e.target.value); setSuccess(false); if (errorMsg) setErrorMsg(''); }}
          disabled={loading}
          aria-invalid={errorMsg ? 'true' : 'false'}
          data-testid={`waitlist-email-input-${variant}`}
          className={`om-input w-full ${isCta ? 'sm:h-14' : ''} ${errorMsg ? 'border-red-400 focus:border-red-500' : ''}`}
        />
        {errorMsg && (
          <div
            data-testid={`waitlist-error-${variant}`}
            className="mt-2 pl-5 text-xs text-red-600"
          >
            {errorMsg}
          </div>
        )}
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid={`waitlist-submit-btn-${variant}`}
        className={`om-btn-primary shrink-0 ${isCta ? 'sm:h-14 sm:px-8' : ''}`}
      >
        {loading ? (
          <>
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-current" />
            <span>{t('waitlist.submitting')}</span>
          </>
        ) : success ? (
          <span>{t('waitlist.submitted')}</span>
        ) : (
          <>
            <span>{t('waitlist.submit')}</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </>
        )}
      </button>
    </form>
  );
};
